import { flowRight } from 'lodash';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import Wix from '../../../app/services/wix-sdk-polyfill';
import * as appLifecycleService from '../../../app/services/app-lifecycle-service';
import { clearUserComments } from '../../../app/actions/clear-user-comments';
import { fetchCommentsBySiteMemberId } from '../../../app/actions/fetch-comments-by-user';
import { setIsLoaded } from '../../../app/actions/set-is-loaded';
import { incrementPostLikeCount } from '../../../app/actions/increment-post-like-count';
import { incrementCommentLikeCount } from '../../../app/actions/increment-comment-like-count';
import AnimatedLoader from '../../../app/components/animated-loader';
import { getIsProfileCommentsLoading } from '../../../app/selectors/is-loading-selectors';
import { getIsProfileCommentsLoaded } from '../../../app/selectors/is-loaded-selectors';
import {
  getUserComments,
  getUserCommentsCount,
  getUserCommentsCursor,
} from '../../../app/selectors/user-comments-selectors';
import { COMMENTS_PER_PAGE } from '@wix/communities-blog-client-common';
import InfiniteListLoader from '../../../app/components/infinite-list-loader';
import CommentList from '../../components/comment-list';
import Loader from '../../../app/components/loader';
import withTranslate from '../../../app/hoc/with-translate';
import fedopsLogger from '../../services/fedops-logger';
import NoItems from '../../../common/components/member-no-items';
import ProfileContentLayout from '../../../common/components/profile-content-layout';

const reset = () => ({ page: 1 });
const increment = (state) => ({ page: state.page + 1 });

class ProfileBlogComments extends Component {
  state = { page: 1 };

  componentDidMount = () => {
    this.fetchUserDataWithCurrentUser().then(appLifecycleService.appLoaded);
    Wix.addEventListener(Wix.Events.PAGE_NAVIGATION_IN, this.fetchUserDataWithCurrentUser);
    fedopsLogger.appLoaded();
  };

  componentWillReceiveProps = ({ siteMemberId: siteSiteMemberId }) => {
    const { siteMemberId } = this.props;
    if (siteMemberId !== siteSiteMemberId) {
      this.setState(reset, () => this.fetchUserData(siteSiteMemberId));
    }
  };

  componentWillUnmount = () => {
    this.props.clearComments();
    this.props.setIsLoaded('profile-comments', '', false);
    Wix.removeEventListener(Wix.Events.PAGE_NAVIGATION_IN, this.fetchUserDataWithCurrentUser);
  };

  fetchUserDataWithCurrentUser = () => {
    return this.fetchUserData(this.props.siteMemberId);
  };

  fetchUserData = (siteMemberId) => {
    this.props.clearComments();
    this.props.setIsLoaded('profile-comments', '', false);
    return this.fetchComments(siteMemberId);
  };

  fetchComments = (siteMemberId = this.props.siteMemberId) => {
    const { page } = this.state;
    return this.props.fetchComments({
      siteMemberId,
      page,
      pageSize: COMMENTS_PER_PAGE,
    });
  };

  handleLoad = () => this.setState(increment, this.fetchComments);

  renderNoComments = () => {
    const { t } = this.props;

    return <NoItems title={t('no-comments.title')} content={t('no-comments.content')} />;
  };

  renderCommentsList = () => {
    const {
      incrementCommentLikeCount,
      isLoading,
      incrementPostLikeCount,
      comments,
      commentsCount,
      commentsCursor,
    } = this.props;

    return (
      <div>
        <CommentList
          comments={comments}
          onCommentLikeClick={incrementCommentLikeCount}
          onPostLikeClick={incrementPostLikeCount}
          showLikeButton={false}
        />
        <InfiniteListLoader
          onLoad={this.handleLoad}
          loader={<Loader />}
          isLoading={isLoading}
          hasMore={comments.length < commentsCount || commentsCursor}
        />
      </div>
    );
  };

  render = () => {
    const { isLoading, isLoaded, comments, t } = this.props;
    return (
      <ProfileContentLayout
        title={t('profile-blog-comments.blog-comments')}
        titleClassName="page-title-color"
        titleFontClassName="page-title-font"
      >
        <AnimatedLoader isLoading={isLoading && !comments.length}>
          {!comments.length && isLoaded ? this.renderNoComments() : this.renderCommentsList()}
        </AnimatedLoader>
      </ProfileContentLayout>
    );
  };
}

ProfileBlogComments.propTypes = {
  siteMemberId: PropTypes.string.isRequired,
  fetchComments: PropTypes.func,
  clearComments: PropTypes.func,
  setIsLoaded: PropTypes.func,
  incrementCommentLikeCount: PropTypes.func,
  incrementPostLikeCount: PropTypes.func,
  comments: PropTypes.array,
  commentsCount: PropTypes.number,
  commentsCursor: PropTypes.number,
  location: PropTypes.object,
  isLoading: PropTypes.bool,
  isLoaded: PropTypes.bool,
  t: PropTypes.func,
};

const mapStateToProps = (state) => ({
  comments: getUserComments(state),
  commentsCount: getUserCommentsCount(state),
  commentsCursor: getUserCommentsCursor(state),
  isLoading: getIsProfileCommentsLoading(state),
  isLoaded: getIsProfileCommentsLoaded(state),
});

const mapDispatchToProps = {
  clearComments: clearUserComments,
  fetchComments: fetchCommentsBySiteMemberId,
  incrementCommentLikeCount,
  incrementPostLikeCount,
  setIsLoaded,
};

// prettier-ignore
export default flowRight(
  connect(mapStateToProps, mapDispatchToProps),
  withTranslate,
)(ProfileBlogComments);
